import { ComponentProps } from "react";
import { datePeriod as defaultDatePeriod } from "constants/bookkeeping";
import { YYYY_MM_DD } from "constants/date";
import { DateFilter } from "components/Filters/DateFilter";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { useGetStartingDateQuery } from "store/apis/chartOfAccounts";
import { DateRangeValue, getDateRangeWithSetting } from "utils/getDateRange";
import { getDatePeriodWithSetting } from "utils/getDatePeriod";

type BooksDateFilterProps = ComponentProps<typeof DateFilter> & {
  allowDatesBeforeBookkeepingStart?: boolean;
};

export const BooksDateFilter = ({
  datePeriod: datePeriodFromProps,
  updateFilter,
  minStartDate,
  allowDatesBeforeBookkeepingStart = false,
  skipDateUpdateOnPeriodChange = false,
  ...rest
}: BooksDateFilterProps) => {
  const entityId = useCurrentEntityId();
  const groupId = useCurrentGroupContext()?.uuid;

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );
  const { data: startDateData } = useGetStartingDateQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );
  const bookkeepingStartDate = startDateData?.start_date;
  const taxYearEnd = bookkeepingSettings?.tax_year_end;

  const datePeriod = getDatePeriodWithSetting(
    datePeriodFromProps || defaultDatePeriod,
    taxYearEnd,
    bookkeepingStartDate
  );

  const bookkeepingStartFallback =
    !allowDatesBeforeBookkeepingStart && bookkeepingStartDate
      ? new Date(bookkeepingStartDate)
      : undefined;

  const effectiveMinStartDate = minStartDate || bookkeepingStartFallback;

  const minStartDateTooltip =
    minStartDate || allowDatesBeforeBookkeepingStart
      ? undefined
      : "This date is before the bookkeeping start date";

  const onUpdateFilter: BooksDateFilterProps["updateFilter"] = (
    name,
    newValue
  ) => {
    updateFilter(name, newValue);

    if (skipDateUpdateOnPeriodChange) return;
    if (name !== "SELECT_PERIOD" || newValue === "CUSTOM") return;

    const { startDate, endDate } = getDateRangeWithSetting(
      newValue as DateRangeValue,
      taxYearEnd,
      bookkeepingStartDate
    );
    updateFilter("START_DATE", startDate.format(YYYY_MM_DD));
    updateFilter("END_DATE", endDate.format(YYYY_MM_DD));
  };

  return (
    <DateFilter
      {...rest}
      datePeriod={datePeriod}
      minStartDate={effectiveMinStartDate}
      minStartDateTooltip={minStartDateTooltip}
      updateFilter={onUpdateFilter}
      skipDateUpdateOnPeriodChange
    />
  );
};
